import TagLinkCell from '@nile/banyan/src/Pages/ServiceCatalog/TagLinkCell';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import {
  AppContext,
  BackgroundIcon,
  ContentDeciderContainer,
  DeleteModal,
  getTranslatedText,
  isAuthorised,
  Loader,
  SkeletonTableLoading,
  TableContainer,
  Tag,
  toast,
  getDefaultValue,
  updateConditions,
  EventEmitter,
  useCheckFeatureGating
} from '@nile/components';
import {
  GET_SERVICE_CATEGORIES,
  AccountClient,
  useQuery,
  GET_WHITELISTED_SERVICE_ITEMS,
  GET_OFFERED_ITEMS_LIST,
  GET_ELIGIBLE_SERVICE_ITEMS
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import { TextCell } from '@nile/mangifera/src/Components/Ticket';
import LinkCell from '@nile/banyan/src/Pages/ServiceCatalog/LinkCell';
import TableView from '@nile/banyan/src/Pages/ServiceCatalog/TableView';
import { Button } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import styled from 'styled-components';
import CurrencyCell from './CurrencyCell';
import InvoiceItemSelectionCell from './InvoiceItemSelectionCell';
import AuditBulkSelectCell from './AuditBulkSelectCell';
import { useFlags } from 'launchdarkly-react-client-sdk';

const ROW_COUNT = 20;

const TableWrapper = styled(TableContainer)`
  .colHeader {
    margin-left: 8px;
  }
`;

const BulkSelectWrapper = styled.div`
  .Superops-Choice {
    padding-top: 0;
    padding-bottom: 0;
  }
`;

function ItemsToInvoiceTable(props) {
  const { intl, selectedSection, renderServiceItemSheet } = props;
  const [localSelectedSection, setLocalSelectedSection] = useState(
    selectedSection
  );
  const selectedSectionRef = React.useRef(0);
  selectedSectionRef.current = localSelectedSection;
  const loading = false;
  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', columns: [] }
  ]);
  const [data, setData] = useState([]);
  const [records, setRecords] = useState([]);
  /**
   * Default options for the list info to use in table
   */
  const [options, setOptions] = useState({
    pageSize: ROW_COUNT,
    page: 1,
    sort: [
      {
        attribute: 'name',
        order: 'DESC'
      }
    ],
    condition: null
  });

  const flags = useFlags();

  const [listLoading, setListLoading] = useState(true);
  const [listInfo, setListInfo] = useState({});
  const listInfoByRef = useRef(null);
  listInfoByRef.current = listInfo;
  const appContext = useContext(AppContext);
  const { user } = appContext;
  const roleFeatures = user?.role?.roleFeatureList || [];

  const contractsNew = useCheckFeatureGating('CONTRACT_V3')?.enabled;

  const setConditions = (condition) => {
    // fetchViewData(undefined, { ...listInfoByRef.current, page: 1, condition });
  };

  const updateAllConditions = (condition, selectedField) => {
    updateConditions({
      condition,
      selectedField,
      viewConditions: listInfoByRef?.current?.condition || null, // any variable in which conditions are stored for this component
      setConditions: setConditions
    });
  };
  const getType = (col) => {
    switch (col.name) {
      case 'selection':
        return 'Check';
      case 'unitPrice':
        return 'Currency';
      default:
        return 'Text';
    }
  };

  const getAccessor = (sItem) => {
    if (sItem?.quantityType === 'HOURS') {
      return sItem?.businessHoursUnitPrice || null;
    }
    if (sItem.sellingPrice) {
      return sItem?.sellingPrice?.details?.[0]?.value;
    }
    return sItem.unitPrice || null;
  };

  /**
   * Method to get Service items.
   * Call this method to fetch/refetch the data on mounting/sorting/filtering
   * @param {object} personalization
   * @param {object} pageOptions
   */
  const fetchViewData = async (personalization, pageOptions) => {
    setListLoading(true);
    if (selectedSectionRef.current === 'audit') {
      const operands = [
        {
          attribute: 'status',
          operator: 'is',
          value: 'APPROVED'
        },
        {
          attribute: 'client.name',
          operator: 'is',
          value: props.client.name
        }
      ];
      if (props?.generateSiteSpecific && props?.site?.name) {
        operands.push({
          attribute: 'site.name',
          operator: 'is',
          value: props.site.name
        });
      }
      const response = await AccountClient.query({
        query: GET_OFFERED_ITEMS_LIST,
        variables: {
          isBillingQueue: true,
          listInfo: {
            page: pageOptions?.page || 1,
            pageSize: 20,
            condition: {
              joinOperator: 'AND',
              operands: operands
            }
          }
        },
        fetchPolicy: 'network-only'
      });
      const cols = [
        {
          name: 'selection',
          label: (
            <BulkSelectWrapper className="flex actr jctr">
              <AuditBulkSelectCell
                allAuditItems={response?.data?.getOfferedItems?.items || []}
                checkDisabledItem={props.checkDisabledRow}
              />
            </BulkSelectWrapper>
          )
        },
        {
          name: contractsNew ? 'serviceCatalogItem.name' : 'serviceItem.name',
          label: getTranslatedText(
            intl,
            'invoiceBuilder.itemSelection.list.name'
          )
        },
        {
          name: contractsNew
            ? 'serviceCatalogItem.description'
            : 'serviceItem.invoiceDescription',
          label: getTranslatedText(
            intl,
            'invoiceBuilder.itemSelection.list.description'
          )
        },
        {
          name: 'unitPrice',
          label: getTranslatedText(
            intl,
            'invoiceBuilder.itemSelection.list.unitPrice'
          )
        }
      ];
      const columns = cols.map((col, idx) => {
        return {
          Header: col.label,
          accessor: col.name,
          // minWidth: col.name === 'subject' ? 400 : 50,
          width: col.name === 'selection' ? 50 : 200,
          type: getType(col),
          id: col.name,
          disableResizing: false,
          className: 'sticky',
          disableSortBy: true,
          editable: false,
          disableFilters: true,
          dynamicAccessor: getAccessor,
          actions: {
            onChange: () => {}
            // onChange: debounce(modifyData, 10)
          },
          field: col,
          isBold: col.name === 'unitPrice'
        };
      });
      setListLoading(false);
      setColumnGroup([{ Header: 'Name', columns }]);
      if (response?.data?.getOfferedItems) {
        setData(response.data.getOfferedItems.items);
        setListInfo({
          ...response.data.getOfferedItems.listInfo,
          condition: pageOptions?.condition || listInfo?.condition || null
        });
        return true;
      }
    } else {
      const condition =
        selectedSectionRef.current === 'all'
          ? null
          : pageOptions?.condition || {
              joinOperator: 'AND',
              operands: [
                {
                  attribute: 'category.name',
                  operator: 'is',
                  value: selectedSectionRef.current
                }
              ]
            };
      const listInfo = {
        page: pageOptions?.page || 1,
        pageSize: 20,
        condition: condition,
        sort: [
          {
            attribute: 'name',
            order: 'ASC'
          }
        ]
      };
      const response = await AccountClient.query({
        query: contractsNew
          ? GET_ELIGIBLE_SERVICE_ITEMS
          : GET_WHITELISTED_SERVICE_ITEMS,
        fetchPolicy: 'network-only',
        variables: contractsNew
          ? {
              input: {
                listInfo
              }
            }
          : {
              clientId: props.client.accountId,
              listInfo
            }
      });
      setListLoading(false);
      const cols = [
        {
          name: 'selection',
          label: '  '
        },
        {
          name: 'name',
          label: getTranslatedText(
            intl,
            'invoiceBuilder.itemSelection.list.name'
          )
        },
        {
          name: contractsNew ? 'description' : 'invoiceDescription',
          label: getTranslatedText(
            intl,
            'invoiceBuilder.itemSelection.list.description'
          )
        },
        {
          name: 'unitPrice',
          label: getTranslatedText(
            intl,
            'invoiceBuilder.itemSelection.list.unitPrice'
          )
        }
      ];
      const columns = cols.map((col, idx) => {
        return {
          Header: col.label,
          accessor: col.name,
          // minWidth: col.name === 'subject' ? 400 : 50,
          width: col.name === 'selection' ? 50 : 200,
          type: getType(col),
          id: col.name,
          disableResizing: false,
          className: 'sticky',
          disableSortBy: true,
          editable: false,
          dynamicAccessor: getAccessor,
          disableFilters: true,
          actions: {
            onChange: () => {}
            // onChange: debounce(modifyData, 10)
          },
          field: col,
          isBold: col.name === 'unitPrice'
        };
      });
      setColumnGroup([{ Header: 'Name', columns }]);
      if (contractsNew) {
        if (response?.data?.getEligibleServiceCatalogItems) {
          setData(response.data.getEligibleServiceCatalogItems.items);
          setListInfo({
            ...response.data.getEligibleServiceCatalogItems.listInfo,
            condition: pageOptions?.condition || listInfo?.condition || null
          });
          return true;
        }
      } else {
        if (response?.data?.getWhitelistedServiceItems) {
          setData(response.data.getWhitelistedServiceItems.items);
          setListInfo({
            ...response.data.getWhitelistedServiceItems.listInfo,
            condition: pageOptions?.condition || listInfo?.condition || null
          });
          return true;
        }
      }
    }
  };

  const fetchFilteredData = (text) => {
    const condition = {
      joinOperator: 'OR',
      operands: [
        {
          attribute: 'name',
          operator: 'contains',
          value: text
        },
        {
          attribute: contractsNew ? 'description' : 'invoiceDescription',
          operator: 'contains',
          value: text
        }
      ]
    };
    fetchViewData(null, { page: 1, pageSize: 20, condition: condition });
  };

  useEffect(() => {
    EventEmitter.subscribe('filterData', fetchFilteredData);
    return function cleanup() {
      EventEmitter.unsubscribe('filterData', fetchFilteredData);
    };
  }, []);

  useEffect(() => {
    setLocalSelectedSection(selectedSection);
  }, [selectedSection]);

  useEffect(() => {
    if (localSelectedSection) {
      fetchViewData();
    }
  }, [localSelectedSection]);

  const defaultColumnCells = {
    Currency: CurrencyCell,
    Check: InvoiceItemSelectionCell
  };

  const setSelectedItems = (items) => {
    // props.setRecords(items);
  };

  const context = {
    setRecords: setSelectedItems,
    viewConditions: listInfoByRef?.current?.condition || null
  };

  return (
    <>
      {renderServiceItemSheet?.(fetchViewData)}
      <TableWrapper>
        <ContentDeciderContainer
          isLoading={loading || !columnGroup[0]?.columns?.length}
          hasContent
          fullHeight
          mainContent={
            <TableView
              noIndicators
              noBulkSelect
              context={context}
              columns={columnGroup}
              data={data || []}
              setData={setData}
              rowCount={ROW_COUNT}
              refreshTable={fetchViewData}
              listInfo={listInfo}
              defaultColumnCells={defaultColumnCells}
              pagination={
                Math.ceil(listInfo.totalCount / listInfo.pageSize) > 1
              }
              pages={Math.ceil(listInfo.totalCount / listInfo.pageSize) || 0}
              recordCount={listInfo.totalCount}
              activePage={listInfo.page || 0}
              isLoading={listLoading}
              fullHeight
              checkDisabledRow={props.checkDisabledRow}
              checkRowHighlight={props.checkRowHighlight}
            />
          }
          loadingContent={<SkeletonTableLoading />}
          notFoundURL={`${envConfig.imageRoot}svg/not-found.svg`}
        />
      </TableWrapper>
    </>
  );
}

export default injectIntl(withRouter(ItemsToInvoiceTable));
